/**
 * Custom PDF generators for MR JERRY
 * These are app-specific PDF functions with custom branding
 */

import html2pdf from "html2pdf.js";
import { fmtPLN, waiterFullName, todayISO } from './helpers';

/**
 * Generate single day tips PDF with custom MR JERRY branding
 * @param {string} targetDate - Date to generate PDF for (YYYY-MM-DD)
 * @param {Array} entries - All tip entries
 * @param {Array} waiters - All waiters
 * @param {string} locationName - Location display name
 * @param {string} logoUrl - Logo URL
 */
export const generateDayPDF = async (targetDate, entries, waiters, locationName, logoUrl) => {
  // Filter entries for specific day
  const dayEntries = entries.filter(e => e.date === targetDate);
  
  if (dayEntries.length === 0) {
    console.log("No entries for this day, skipping PDF generation");
    return;
  }

  // Calculate totals for this day
  let totalAmount = 0, totalWaiter = 0, totalKitchen = 0;
  for (const e of dayEntries) {
    totalAmount += Number(e.amount || 0);
    totalWaiter += Number(e.waiter || 0);
    totalKitchen += Number(e.kitchen || 0);
  }

  // Build waiter summary for this day
  const waiterById = new Map(waiters.map(w => [w.id, w]));
  const waiterMap = new Map();
  for (const e of dayEntries) {
    const wid = e.waiterId;
    if (!waiterMap.has(wid)) {
      waiterMap.set(wid, { waiterId: wid, amount: 0, waiter: 0, kitchen: 0, count: 0 });
    }
    const wData = waiterMap.get(wid);
    wData.amount += Number(e.amount || 0);
    wData.waiter += Number(e.waiter || 0);
    wData.kitchen += Number(e.kitchen || 0);
    wData.count += 1;
  }

  const waiterRows = Array.from(waiterMap.values()).map(t => {
    const w = waiterById.get(t.waiterId);
    return `<tr>
      <td style="padding:12px;border:1px solid #ddd;text-align:left">${w ? waiterFullName(w) : "?"}</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:right;font-weight:bold">${fmtPLN(t.amount)} zł</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:right;color:#16a34a">${fmtPLN(t.waiter)} zł</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:right;color:#2563eb">${fmtPLN(t.kitchen)} zł</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:center">${t.count}</td>
    </tr>`;
  }).join("");

  const html = `
    <div style="font-family:Arial,sans-serif;padding:30px;max-width:800px;color:#1a1a1a;background:#fff">
      <!-- Header -->
      <div style="text-align:center;border-bottom:3px solid #d4af37;padding-bottom:20px;margin-bottom:25px">
        <img src="${logoUrl}" style="width:80px;height:80px;border-radius:50%"/>
        <h1 style="margin:15px 0 5px;font-size:28px;color:#1a1a1a">MR JERRY</h1>
        <p style="color:#666;margin:0;font-size:14px">Podsumowanie napiwków dnia (Kelner 60% / Kuchnia 40%)</p>
        <p style="color:#888;margin:8px 0 0;font-size:12px">Lokal: ${locationName}</p>
        <p style="color:#888;margin:5px 0 0;font-size:12px">Data: ${targetDate}</p>
        <p style="color:#888;margin:5px 0 0;font-size:11px">Wygenerowano: ${new Date().toLocaleString()}</p>
      </div>

      <!-- Summary Box -->
      <div style="background:#f8f9fa;border:2px solid #d4af37;border-radius:12px;padding:25px;margin-bottom:30px">
        <h2 style="margin:0 0 20px;font-size:18px;color:#333;text-align:center">PODSUMOWANIE DNIA</h2>
        <table style="width:100%;border-collapse:collapse">
          <tr>
            <td style="padding:15px;text-align:center;width:25%">
              <div style="font-size:28px;font-weight:bold;color:#d4af37">${fmtPLN(totalAmount)} zł</div>
              <div style="font-size:12px;color:#666;margin-top:5px">SUMA NAPIWKÓW</div>
            </td>
            <td style="padding:15px;text-align:center;width:25%">
              <div style="font-size:24px;font-weight:bold;color:#16a34a">${fmtPLN(totalWaiter)} zł</div>
              <div style="font-size:12px;color:#666;margin-top:5px">KELNERZY (60%)</div>
            </td>
            <td style="padding:15px;text-align:center;width:25%">
              <div style="font-size:24px;font-weight:bold;color:#2563eb">${fmtPLN(totalKitchen)} zł</div>
              <div style="font-size:12px;color:#666;margin-top:5px">KUCHNIA (40%)</div>
            </td>
            <td style="padding:15px;text-align:center;width:25%">
              <div style="font-size:24px;font-weight:bold;color:#333">${dayEntries.length}</div>
              <div style="font-size:12px;color:#666;margin-top:5px">WPISÓW</div>
            </td>
          </tr>
        </table>
      </div>

      <!-- Waiters Table -->
      <h3 style="font-size:16px;color:#333;margin:25px 0 15px;padding-bottom:8px;border-bottom:2px solid #eee">PODSUMOWANIE KELNERÓW</h3>
      <table style="width:100%;border-collapse:collapse;font-size:14px">
        <thead>
          <tr style="background:#d4af37">
            <th style="padding:14px;text-align:left;color:#000;font-weight:bold;border:1px solid #c9a230">Kelner</th>
            <th style="padding:14px;text-align:right;color:#000;font-weight:bold;border:1px solid #c9a230">Suma</th>
            <th style="padding:14px;text-align:right;color:#000;font-weight:bold;border:1px solid #c9a230">Kelner 60%</th>
            <th style="padding:14px;text-align:right;color:#000;font-weight:bold;border:1px solid #c9a230">Kuchnia 40%</th>
            <th style="padding:14px;text-align:center;color:#000;font-weight:bold;border:1px solid #c9a230">Wpisów</th>
          </tr>
        </thead>
        <tbody>
          ${waiterRows || '<tr><td colspan="5" style="padding:20px;text-align:center;color:#666;border:1px solid #ddd">Brak danych</td></tr>'}
        </tbody>
      </table>

      <!-- Footer -->
      <div style="margin-top:40px;padding-top:20px;border-top:2px solid #eee;text-align:center;color:#888;font-size:11px">
        <p style="margin:0">MR JERRY • System zarządzania napiwkami</p>
        <p style="margin:5px 0 0">Kelner 60% / Kuchnia 40%</p>
      </div>
    </div>
  `;

  const container = document.createElement("div");
  container.innerHTML = html;
  document.body.appendChild(container);
  await html2pdf().set({ 
    margin: 15, 
    filename: `mrjerry_napiwki_${targetDate}.pdf`, 
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  }).from(container.firstElementChild).save();
  document.body.removeChild(container);
};

/**
 * Generate billard report PDF with custom MR JERRY branding
 * @param {string} startDate - Start date filter (YYYY-MM-DD)
 * @param {string} endDate - End date filter (YYYY-MM-DD)
 * @param {Array} billardEntries - All billard entries
 * @param {string} locationName - Location display name
 * @param {string} logoUrl - Logo URL
 */
export const generateBillardPDF = async (startDate, endDate, billardEntries, locationName, logoUrl) => {
  // Filter billard entries for date range
  const filtered = billardEntries.filter(e => {
    if (startDate && e.date < startDate) return false;
    if (endDate && e.date > endDate) return false;
    return true;
  });

  if (filtered.length === 0) {
    alert("Brak wpisów bilarda w tym okresie");
    return;
  }

  // Calculate total
  const total = filtered.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  // Build rows
  const rows = filtered.map(e => `
    <tr>
      <td style="padding:12px;border:1px solid #ddd;text-align:left">${e.date}</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:right;font-weight:bold;color:#0ea5e9">${fmtPLN(e.amount)} zł</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:left">${e.notes || "-"}</td>
      <td style="padding:12px;border:1px solid #ddd;text-align:left">${e.addedBy}</td>
    </tr>
  `).join("");

  const dateRange = startDate && endDate 
    ? `${startDate} - ${endDate}`
    : startDate 
      ? `od ${startDate}`
      : endDate
        ? `do ${endDate}`
        : "Wszystkie";

  const html = `
    <div style="font-family:Arial,sans-serif;padding:30px;max-width:800px;color:#1a1a1a;background:#fff">
      <!-- Header -->
      <div style="text-align:center;border-bottom:3px solid #0ea5e9;padding-bottom:20px;margin-bottom:25px">
        <img src="${logoUrl}" style="width:80px;height:80px;border-radius:50%"/>
        <h1 style="margin:15px 0 5px;font-size:28px;color:#1a1a1a">MR JERRY - BILLARD</h1>
        <p style="color:#666;margin:0;font-size:14px">Raport wpływów z sali billardowej</p>
        <p style="color:#888;margin:8px 0 0;font-size:12px">Lokal: ${locationName}</p>
        <p style="color:#888;margin:5px 0 0;font-size:12px">Okres: ${dateRange}</p>
        <p style="color:#888;margin:5px 0 0;font-size:11px">Wygenerowano: ${new Date().toLocaleString()}</p>
      </div>

      <!-- Summary Box -->
      <div style="background:#f0f9ff;border:2px solid #0ea5e9;border-radius:12px;padding:25px;margin-bottom:30px">
        <h2 style="margin:0 0 20px;font-size:18px;color:#333;text-align:center">PODSUMOWANIE</h2>
        <div style="text-align:center">
          <div style="font-size:36px;font-weight:bold;color:#0ea5e9">${fmtPLN(total)} zł</div>
          <div style="font-size:14px;color:#666;margin-top:5px">Suma wpływów z bilarda</div>
          <div style="font-size:18px;font-weight:bold;color:#333;margin-top:15px">${filtered.length} wpisów</div>
        </div>
      </div>

      <!-- Table -->
      <h3 style="font-size:16px;color:#333;margin:25px 0 15px;padding-bottom:8px;border-bottom:2px solid #eee">SZCZEGÓŁOWA LISTA WPISÓW</h3>
      <table style="width:100%;border-collapse:collapse;font-size:14px">
        <thead>
          <tr style="background:#0ea5e9">
            <th style="padding:14px;text-align:left;color:#fff;font-weight:bold;border:1px solid #0284c7">Data</th>
            <th style="padding:14px;text-align:right;color:#fff;font-weight:bold;border:1px solid #0284c7">Kwota</th>
            <th style="padding:14px;text-align:left;color:#fff;font-weight:bold;border:1px solid #0284c7">Uwagi</th>
            <th style="padding:14px;text-align:left;color:#fff;font-weight:bold;border:1px solid #0284c7">Dodał</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>

      <!-- Footer -->
      <div style="margin-top:40px;padding-top:20px;border-top:2px solid #eee;text-align:center;color:#888;font-size:11px">
        <p style="margin:0">MR JERRY • System zarządzania billard</p>
        <p style="margin:5px 0 0">Sala billardowa - Nowy Sącz</p>
      </div>
    </div>
  `;

  const container = document.createElement("div");
  container.innerHTML = html;
  document.body.appendChild(container);
  
  const filename = startDate && endDate 
    ? `mrjerry-billard-${startDate}-${endDate}.pdf`
    : `mrjerry-billard-${todayISO()}.pdf`;
  
  await html2pdf().set({ 
    margin: 15, 
    filename, 
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  }).from(container.firstElementChild).save();
  document.body.removeChild(container);
};
